import {CountryEnum, PositionAreaEnum, PositionEnum} from "@/modules/base/enums";
import {FormationEnum} from "@/modules/team/enums";
import {GroundSlot} from "@/modules/club-grounds/entity";
import {GroundSlotTypeEnum} from "@/modules/club-grounds/enums";
import {Stadium, Terrace} from "@/modules/stadium/entity";
import {TerraceTypeEnum} from "@/modules/stadium/enums";
import {FoundationDefinitionType} from "@/modules/stadium/types";
import {Player, Team} from "@/modules/team/entity";
import {CardEnum} from "@/modules/match/enums";
import {AIQualityLevelEnum} from "@/modules/ai/enums";
import {AIQualityServiceDefinitionType} from "@/modules/ai/types";
import BestMatchingLineupper from "@/modules/ai/services/lineupper/BestMatchingLineupper";
import RandomLineupper from "@/modules/ai/services/lineupper/RandomLineupper";
import {Negotiator, StaffMember} from "@/modules/staff/entity";
import {Scout} from "@/modules/scouting/entity";
import chance from "@/modules/base/services/chance";
import {Coach} from "@/modules/training/entity";
import {GateTypeEnum} from "@/modules/action-points/enums";
import {Competition, CupCompetition, CupWithGroupCompetition, LeagueCompetition} from "@/modules/competitions/entity";
import {Match} from "@/modules/match/entity";
import TeamLevelTransferDecider from "@/modules/ai/services/transferDecider/CurrentSkillTransferDecider";

export default {
  // @ts-ignore
  debug: process.env.VUE_APP_DEBUG,
  teams: {
    // if teams has less than this count of players, new players will be generated
    minPlayerCount: (team: Team) => 22,
    // percentage of players in team which supposed to be not foreigners
    percentageForeigners: (team: Team) => 50,
  },
  coins: {
    // if enabled, you will be able to buy coins
    enabled: true,
    // users team will start with this amount of coins
    startAmount: 100,
  },
  general: {
    auto_save: {
      // this will show auto save toast for minimum amount of time so the user recognizes
      minimum_wait_for: 2000,
    },
    // countries player can start play in
    playableCountries: [
      CountryEnum.GB,
      CountryEnum.ES,
      CountryEnum.DE,
      CountryEnum.IT,
      CountryEnum.FR,
    ],
    international: {
      // countries for which international teams will be generated. mapping is 0-1 (1 is best team)
      countries: {
        [CountryEnum.GB]: 100,
        [CountryEnum.ES]: 100,
        [CountryEnum.DE]: 100,
        [CountryEnum.IT]: 100,
        [CountryEnum.FR]: 90,
        //
        [CountryEnum.BE]: 70,
        [CountryEnum.HR]: 70,
        [CountryEnum.CZ]: 70,
        [CountryEnum.DK]: 60,
        [CountryEnum.GR]: 60,
        [CountryEnum.NL]: 90,
        [CountryEnum.PL]: 60,
        [CountryEnum.PT]: 90,
        [CountryEnum.RU]: 60,
        [CountryEnum.RO]: 50,
        [CountryEnum.RS]: 50,
        [CountryEnum.SE]: 50,
        [CountryEnum.CH]: 80,
        [CountryEnum.UA]: 70,
      },
      teamsPerCountry: 2,
    },
    // start bank balance
    startBalance: 20000,
    // season start year
    seasonStartYear: 2022,
  },
  ai: {
    services: {
      [AIQualityLevelEnum.WORST]: {
        lineupper: RandomLineupper,
        transferDecider: TeamLevelTransferDecider,
      },
      [AIQualityLevelEnum.BAD]: {
        lineupper: BestMatchingLineupper,
        transferDecider: TeamLevelTransferDecider,
      },
      [AIQualityLevelEnum.AVERAGE]: {
        lineupper: BestMatchingLineupper,
        transferDecider: TeamLevelTransferDecider,
      },
      [AIQualityLevelEnum.GOOD]: {
        lineupper: BestMatchingLineupper,
        transferDecider: TeamLevelTransferDecider,
      },
      [AIQualityLevelEnum.BEST]: {
        lineupper: BestMatchingLineupper,
        transferDecider: TeamLevelTransferDecider,
      },
    } as { [key in AIQualityLevelEnum]: AIQualityServiceDefinitionType }
  },
  transferMarket: {
    // random are offers for not listed players
    random: {
      // chance of receiving an offer for a listing per day
      percentChanceForOffer: 5,
      // this is the range of offers which are created from computer teams to users player listings
      aiOfferPriceRange: {
        min: 70,
        max: 150,
      },
      // x random transfers will be executed each week with random teams
      transfersPerWeek: (teamCount: number) => {
        return Math.ceil(teamCount / 52 * 5);
      },
    },
    listings: {
      // chance of receiving an offer for a listing per day
      percentChanceForOffer: 10,
      // there will be not more active offers from computer teams to your own listing
      offerLimitPerListing: 5,
      // this is the range of offers which are created from computer teams to users player listings
      aiOfferPriceRange: {
        min: 80,
        max: 100,
      },
      // target count of ongoing listings which will be aimed to match on the transfer market
      targetCount: (listingCount: number, teamCount: number) => {
        return teamCount;
      },
      // this amount of listing will be removed once a week to be replaced by new listings
      removeCount: (listingCount: number, teamCount: number) => {
        return listingCount;
        return Math.ceil(teamCount * .05);
      },
    },
    directTransfer: {
      // direct AI transfer player price range
      priceRange: {
        min: .8,
        max: 2,
      }
    }
  },
  stadium: {
    startCapacity: 200,
    entranceFee: {
      min: 0,
      max: 65,
    },
    costs: {
      terrace: (terrace: Terrace, seatsTarget: number) => 1000 + 10 * (seatsTarget - terrace.seats),
      foundation: (stadium: Stadium) => 1000 * (stadium.foundationLevel + 1),
    },
    durations: {
      terrace: (terrace: Terrace, seatsTarget: number) => 30 + (seatsTarget - terrace.seats) / 100,
      foundation: (stadium: Stadium) => 30 * (stadium.foundationLevel + 1),
    },
    foundation: {
      maxLevel: 5,
    },
    spectators: {
      terracesFactor: {
        [TerraceTypeEnum.BLOCK_NORTH_WEST]: 0.75,
        [TerraceTypeEnum.BLOCK_NORTH]: 1,
        [TerraceTypeEnum.BLOCK_NORTH_EAST]: 0.75,
        [TerraceTypeEnum.BLOCK_EAST]: 1.25,
        [TerraceTypeEnum.BLOCK_SOUTH_EAST]: 0.75,
        [TerraceTypeEnum.BLOCK_SOUTH]: 1,
        [TerraceTypeEnum.BLOCK_SOUTH_WEST]: 0.75,
        [TerraceTypeEnum.BLOCK_WEST]: 1.25,
      },
    },
    foundations: [
      {
        [TerraceTypeEnum.BLOCK_NORTH_WEST]: {available: false},
        [TerraceTypeEnum.BLOCK_NORTH]: {available: false},
        [TerraceTypeEnum.BLOCK_NORTH_EAST]: {available: false},
        [TerraceTypeEnum.BLOCK_EAST]: {available: true, limit: 1000, entranceFee: 5},
        [TerraceTypeEnum.BLOCK_SOUTH_EAST]: {available: false},
        [TerraceTypeEnum.BLOCK_SOUTH]: {available: false},
        [TerraceTypeEnum.BLOCK_SOUTH_WEST]: {available: false},
        [TerraceTypeEnum.BLOCK_WEST]: {available: false},
      },
      {
        [TerraceTypeEnum.BLOCK_NORTH_WEST]: {available: false},
        [TerraceTypeEnum.BLOCK_NORTH]: {available: true, limit: 1000, entranceFee: 5},
        [TerraceTypeEnum.BLOCK_NORTH_EAST]: {available: false},
        [TerraceTypeEnum.BLOCK_EAST]: {available: true, limit: 2000, entranceFee: 8},
        [TerraceTypeEnum.BLOCK_SOUTH_EAST]: {available: false},
        [TerraceTypeEnum.BLOCK_SOUTH]: {available: true, limit: 1000, entranceFee: 5},
        [TerraceTypeEnum.BLOCK_SOUTH_WEST]: {available: false},
        [TerraceTypeEnum.BLOCK_WEST]: {available: true, limit: 2000, entranceFee: 8},
      },
      {
        [TerraceTypeEnum.BLOCK_NORTH_WEST]: {available: true, limit: 1250, entranceFee: 8},
        [TerraceTypeEnum.BLOCK_NORTH]: {available: true, limit: 6000, entranceFee: 15},
        [TerraceTypeEnum.BLOCK_NORTH_EAST]: {available: true, limit: 1250, entranceFee: 8},
        [TerraceTypeEnum.BLOCK_EAST]: {available: true, limit: 4000, entranceFee: 12},
        [TerraceTypeEnum.BLOCK_SOUTH_EAST]: {available: true, limit: 1250, entranceFee: 8},
        [TerraceTypeEnum.BLOCK_SOUTH]: {available: true, limit: 6000, entranceFee: 15},
        [TerraceTypeEnum.BLOCK_SOUTH_WEST]: {available: true, limit: 1250, entranceFee: 8},
        [TerraceTypeEnum.BLOCK_WEST]: {available: true, limit: 4000, entranceFee: 12},
      },
      {
        [TerraceTypeEnum.BLOCK_NORTH_WEST]: {available: true, limit: 5000, entranceFee: 15},
        [TerraceTypeEnum.BLOCK_NORTH]: {available: true, limit: 7500, entranceFee: 20},
        [TerraceTypeEnum.BLOCK_NORTH_EAST]: {available: true, limit: 5000, entranceFee: 15},
        [TerraceTypeEnum.BLOCK_EAST]: {available: true, limit: 12500, entranceFee: 25},
        [TerraceTypeEnum.BLOCK_SOUTH_EAST]: {available: true, limit: 5000, entranceFee: 15},
        [TerraceTypeEnum.BLOCK_SOUTH]: {available: true, limit: 7500, entranceFee: 20},
        [TerraceTypeEnum.BLOCK_SOUTH_WEST]: {available: true, limit: 5000, entranceFee: 15},
        [TerraceTypeEnum.BLOCK_WEST]: {available: true, limit: 12500, entranceFee: 25},
      },
      {
        [TerraceTypeEnum.BLOCK_NORTH_WEST]: {available: true, limit: 10000, entranceFee: 25},
        [TerraceTypeEnum.BLOCK_NORTH]: {available: true, limit: 17500, entranceFee: 35},
        [TerraceTypeEnum.BLOCK_NORTH_EAST]: {available: true, limit: 10000, entranceFee: 25},
        [TerraceTypeEnum.BLOCK_EAST]: {available: true, limit: 27500, entranceFee: 45},
        [TerraceTypeEnum.BLOCK_SOUTH_EAST]: {available: true, limit: 10000, entranceFee: 25},
        [TerraceTypeEnum.BLOCK_SOUTH]: {available: true, limit: 17250, entranceFee: 35},
        [TerraceTypeEnum.BLOCK_SOUTH_WEST]: {available: true, limit: 10000, entranceFee: 25},
        [TerraceTypeEnum.BLOCK_WEST]: {available: true, limit: 27500, entranceFee: 45},
      },
    ] as FoundationDefinitionType[]
  },
  clubGrounds: {
    totalSlots: 12,
    costs: {
      demolish: (slot: GroundSlot) => 100,
      unlock: (slot: GroundSlot) => slot.id * 1000,
      develop: (slot: GroundSlot, type: GroundSlotTypeEnum) => (Object.values(GroundSlotTypeEnum).indexOf(type) + 1) * 1500,
    },
    durations: {
      demolish: (slot: GroundSlot) => slot.id * 1 * 30,
      unlock: (slot: GroundSlot) => slot.id * 1 * 30,
      develop: (slot: GroundSlot, type: GroundSlotTypeEnum) => slot.id * 1 * 30 * (Object.values(GroundSlotTypeEnum).indexOf(type) + 1),
    },
    effects: {
      headquaters: {
        negotiationProgress: 5,
      },
      office: {
        additionalStaffMemberSlots: 4,
      },
      publicRelations: {
        sponsoringBaseOffer: 10,
      },
      scouting: {
        scoutingProgress: 4,
      },
      training: {
        trainingProgress: 2,
      },
      youth: {
        // under which age players will benefit from this effect (<=)
        ageLimit: 18,
        trainingProgress: 3,
      },
    },
  },
  formations: {
    all: {
      [FormationEnum.F_433]: {
        11: {position: PositionEnum.LW, x: 20, y: 9},
        10: {position: PositionEnum.FW, x: 50, y: 3},
        9: {position: PositionEnum.RW, x: 80, y: 9},
        8: {position: PositionEnum.LM, x: 20, y: 29},
        7: {position: PositionEnum.CM, x: 50, y: 31},
        6: {position: PositionEnum.RM, x: 80, y: 29},
        5: {position: PositionEnum.LD, x: 15, y: 53},
        4: {position: PositionEnum.CD, x: 35, y: 68},
        3: {position: PositionEnum.CD, x: 65, y: 68},
        2: {position: PositionEnum.RD, x: 85, y: 53},
        1: {position: PositionEnum.GK, x: 50, y: 84},
      },
      [FormationEnum.F_41212]: {
        11: {position: PositionEnum.FW, x: 35, y: 3},
        10: {position: PositionEnum.FW, x: 65, y: 3},
        9: {position: PositionEnum.LM, x: 15, y: 31},
        8: {position: PositionEnum.OM, x: 50, y: 22},
        7: {position: PositionEnum.DM, x: 50, y: 43},
        6: {position: PositionEnum.RM, x: 85, y: 31},
        5: {position: PositionEnum.LD, x: 15, y: 53},
        4: {position: PositionEnum.CD, x: 35, y: 68},
        3: {position: PositionEnum.CD, x: 65, y: 68},
        2: {position: PositionEnum.RD, x: 85, y: 53},
        1: {position: PositionEnum.GK, x: 50, y: 84},
      },
    },
    positions: {
      // punishment for wrong positions, will be multiplied by skill
      punishment: {
        wrong: .5,
        similar: .75,
      },
      // similar positions will be not pushed as wrong position
      similar: {
        [PositionEnum.GK]: [],
        [PositionEnum.LD]: [PositionEnum.LM, PositionEnum.CD],
        [PositionEnum.CD]: [PositionEnum.DM, PositionEnum.LD, PositionEnum.RD],
        [PositionEnum.RD]: [PositionEnum.RM, PositionEnum.CD],
        [PositionEnum.LM]: [PositionEnum.LD, PositionEnum.LW],
        [PositionEnum.DM]: [PositionEnum.CM, PositionEnum.OM, PositionEnum.CD],
        [PositionEnum.CM]: [PositionEnum.DM, PositionEnum.OM],
        [PositionEnum.OM]: [PositionEnum.DM, PositionEnum.CM],
        [PositionEnum.RM]: [PositionEnum.RW, PositionEnum.RD],
        [PositionEnum.LW]: [PositionEnum.LM, PositionEnum.FW],
        [PositionEnum.FW]: [PositionEnum.LW, PositionEnum.RW],
        [PositionEnum.RW]: [PositionEnum.RM, PositionEnum.FW],
      }
    },
  },
  ui: {
    actionScreens: {
      // delay of resolving the action screen promise, so there are executive action screens with animations
      // todo maybe remove animations from action screens, can get rid of the delay
      minTimeBetweenActionScreens: 300,
      // duration of action screen animation
      animationDuration: .3,
    },
    notifications: {
      // how many notifications are visible on the notifications popover
      notificationsViewLimit: 5,
    },
    // screen when preloading assets
    loadingScreen: {
      waitBefore: 1000,
      waitAfter: 1000,
    }
  },
  match: {
    evaluation: {
      // reduce players energy per tick
      energyReductionPerTick: (p: Player) => {
        const penaltyFromAge = 27;
        const energyReductionPerGame = 30;

        return energyReductionPerGame / 90 * Math.max(p.age, penaltyFromAge) / penaltyFromAge;
      },
      // chance of striking own goal in game
      chanceOwnGoal: 5,
      // for when displaying the incident to continue evaluating or stepping to next incident
      waitWhenDisplayingIncident: 250,

      averageYellowCardPerMatch: 3,
      averageRedPerMatch: 0.15,
      playerCountLimit: 7,

      skillBased: {
        // average amount of chances in the whole match
        totalChances: (match: Match) => 15,
      }
    },
  },
  season: {
    // milliseconds between evaluations of the days
    waitBetweenDays: 0,
  },
  training: {
    // player talent factor which will be multiplied by the training progress
    talentFactor: 1 / 1000,
    // values will be randomized a bit. e.g. 10 range will make 95%-105% for progress values
    randomPercentRange: 10,
    // one training action will cost of according currency
    actionPointsCosts: 10,
    // will reduce the energy of the player by this amount when training a single unit
    energyReduction: (player: Player) => 5,
  },
  players: {
    contract: {
      terminationFee: (player: Player) => player.wage * player.contractYears! / 2 * player.greediness / 50,
      maxYears: 5,
      endCareer: {
        minAge: 33,
        chance: (player: Player) => {
          switch (player.age) {
            case 33:
              return chance.bool({likelihood: 1});
            case 34:
              return chance.bool({likelihood: 3});
            case 35:
              return chance.bool({likelihood: 5});
            case 36:
              return chance.bool({likelihood: 8});
            default:
              return chance.bool({likelihood: 15});
          }
        }
      },
      contractNegotiation: {
        // when negotiating contract with player this base price will set
        baseOncePrice: (player: Player) => player.marketValue * .1,
      },
      // this is the chance the player will extend expiring contracts at the club, if not, the player is released
      chanceOfExtendingContract: (player: Player) => .9,
    },
    progress: {
      // player skill progress is calculated once per week
      baseMinProgressPerWeek: 0,
      baseMaxProgressPerWeek: 0.02,
      // player talent factor which will be multiplied by the base progress
      talentFactor: 1 / 100,
      // skill reduction depends on age per month
      ageSkillReduction: (player: Player) => {
        if (player.age < 30) {
          return player.skill;
        }

        return player.skill * (100 - chance.floating({min: 1.5, max: 2.5})) / 100;
      },
    },
    form: {
      baseFormChangeRange: {min: -25, max: 25},
      formChangeRangeFromBaseForm: {min: -10, max: 10},
      formChangeDateRangeInDays: {min: 30, max: 90},
      /**
       * 0 form will result in -50% of the skill
       * 50 form does nothing
       * 100 form will result in +50% of skill
       *
       * this value is factor of this effect. 0.5 in this value will result in -25% / +25% for given example
       */
      effectOnSkillFactor: .5,
    },
    energy: {
      // daily gain of the player
      dailyEnergyGain: (player: Player) => {
        const penaltyFromAge = 27;

        return chance.integer({min: 3, max: 6}) * penaltyFromAge / Math.max(player.age, penaltyFromAge);
      },
      // as factor, will be multiplied with effective skill
      effectOnSkillFactory: (player: Player) => {
        let energyLimitEffectStarts = 50;

        if (player.energy > energyLimitEffectStarts) {
          return 0;
        }

        return (player.energy - energyLimitEffectStarts) / energyLimitEffectStarts;
      },
    },
  },
  sponsoring: {
    // all sponsoring prices will be multiplied by this factor
    prestigeFactor: 1 / 100,
    // values will be randomized a bit. e.g. 10 range will make 95%-105% for base values
    randomPercentRange: 6,
    // base values for sponsoring depends on the risk
    types: {
      safe: {
        perLeagueMatch: 210,
        perLeagueVictory: 75,
        perCupMatch: 250,
        perCupVictory: 100,
        once: 2000,
      },
      balanced: {
        perLeagueMatch: 150,
        perLeagueVictory: 225,
        perCupMatch: 200,
        perCupVictory: 275,
        once: 1250,
      },
      risky: {
        perLeagueMatch: 50,
        perLeagueVictory: 460,
        perCupMatch: 25,
        perCupVictory: 500,
        once: 250,
      },
    }
  },
  youth: {
    // likelihood of another team recruiting a youth player of users team
    otherTeamRecruitmentLikelihood: 2 / 365 * 100,
    scouting: {
      region: {
        // range of progress of scouting each day when a scout is assigned
        progressRangePerAction: {
          min: 10,
          max: 20,
          fixed: 2,
        },
      },
      youthPlayer: {
        // range of progress of scouting each action
        progressRangePerAction: {
          min: 10,
          max: 20,
          fixed: 2,
        },
      }
    }
  },
  scouting: {
    actionPointsCosts: {
      region: 4,
      youthPlayer: 6,
      // scouting talent of regular player
      talent: 5,
      // scouting newcomers at the beginning of the season
      newcomers: 15,
    }
  },
  negotiation: {
    actionPointsCosts: {
      // all negotiation for one negotiation action
      general: 2,
      // recruit newcomer at the beginning of a new season
      newcomers: 15,
    }
  },
  competitions: {
    schedules: [
      {
        competition: LeagueCompetition,
        schedule: [
          [11, 63],
          [13, 65],
          [15, 67],
          [17, 69],
          [19, 71],
          [21, 73],
          [23, 75],
          [25, 77],
          [27, 79],
          [29, 81],
          [31, 83],
          [33, 85],
          [35, 87],
          [37, 89],
          [39, 91],
          [41, 93],
          [43, 95],
        ],
      },
      {
        competition: CupCompetition,
        schedule: [
          [10, 14],
          [18, 22],
          [34, 38],
          [66, 70],
          [82, 86],
          [97, 98],
        ],
      },
      {
        competition: CupWithGroupCompetition,
        schedule: [
          // group
          [20, 32],
          [24, 36],
          [28, 40],
          // ko
          [68, 72],
          [76, 80],
          [84, 88],
          [99, 101],
        ],
      }
    ] as { competition: new (...args: any[]) => Competition, schedule: [number, number][] }[],
    suspensions: {
      yellowLimit: 5,
      durations: {
        [CardEnum.YELLOW]: {min: 1, max: 1},
        [CardEnum.YELLOW_RED]: {min: 1, max: 1},
        [CardEnum.RED]: {min: 3, max: 5},
      },
    },
  },
  staff: {
    // staff will end career,
    willEndCareer: (m: StaffMember) => {
      const minAge = 50;
      const maxAge = 70;

      return chance.bool({likelihood: Math.max(0, m.age - minAge) / (maxAge - minAge) * 100});
    },
    actionPointGeneration: {
      // this is the amount of what a single staff member will generate
      negotiating: (negotiator: Negotiator) => chance.floating({min: 0.8, max: 1.2}) * negotiator.skill / 100,
      scouting: (scout: Scout) => chance.floating({min: 0.8, max: 1.2}) * scout.skill / 100,
      training: (coach: Coach) => {
        return chance.floating({min: 0.8, max: 1.2}) * coach.skill * {
          [PositionAreaEnum.GK]: .2,
          [PositionAreaEnum.DF]: .9,
          [PositionAreaEnum.MF]: 1,
          [PositionAreaEnum.AT]: .8,
        }[coach.positionArea!];
      },
      // training all means the coach without special position area -> should be less than a specialist
      trainingAll: (coach: Coach, targetPositionArea: PositionAreaEnum) => {
        return chance.floating({min: 0.8, max: 1.2}) * coach.skill * {
          [PositionAreaEnum.GK]: .2,
          [PositionAreaEnum.DF]: .9,
          [PositionAreaEnum.MF]: 1,
          [PositionAreaEnum.AT]: .8,
        }[targetPositionArea] * {
          [PositionAreaEnum.GK]: .1,
          [PositionAreaEnum.DF]: .2,
          [PositionAreaEnum.MF]: .2,
          [PositionAreaEnum.AT]: .2,
        }[targetPositionArea];
      },
    },
    terminateContract: {
      costNegotiationActionPoints: (staffMembers: StaffMember[] | StaffMember): number => {
        return 5 * (Array.isArray(staffMembers) ? staffMembers.length : 1);
      }
    },
  },
  currency: {
    // exchange rate of coins to other currencies
    coinsExchangeRate: (type: GateTypeEnum, coins: number) => coins * 100,
  }
};
